import React, { useState, useEffect, useCallback } from 'react';
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "@/hooks/use-toast";
import { 
  Plus, 
  Trash2, 
  MapPin, 
  Filter,
  Layers,
  FileText,
  RefreshCw,
  AlertCircle,
} from 'lucide-react';

const VALID_VERTICALS = [
  { value: "mortgage_protection", label: "Mortgage Protection" },
  { value: "final_expense", label: "Final Expense" },
  { value: "medicare", label: "Medicare" },
  { value: "term_life", label: "Term Life" },
  { value: "whole_life", label: "Whole Life" },
];

/**
 * Vertical Mappings Manager
 * 
 * Admin UI for managing campaign/form → vertical mappings.
 * Used for deterministic vertical auto-classification of new leads.
 */
const VerticalMappingsManager = ({ tenantId }) => {
  const [mappings, setMappings] = useState([]);
  const [availableCampaigns, setAvailableCampaigns] = useState([]);
  const [availableForms, setAvailableForms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedMapping, setSelectedMapping] = useState(null);
  
  // Create form state
  const [newMappingType, setNewMappingType] = useState("campaign");
  const [newMappingId, setNewMappingId] = useState("");
  const [newMappingVertical, setNewMappingVertical] = useState("");
  const [creating, setCreating] = useState(false);

  // Fetch all data
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    // P0 TENANT ISOLATION: Pass tenant_id for SuperAdmin cross-tenant access
    const params = tenantId ? { tenant_id: tenantId } : {};
    try {
      const [mappingsRes, campaignsRes, formsRes] = await Promise.all([
        api.get('/vertical-mappings', { params }),
        api.get('/vertical-mappings/meta/available-campaigns', { params }),
        api.get('/vertical-mappings/meta/available-forms', { params }),
      ]);
      
      setMappings(mappingsRes.data?.mappings || []);
      setAvailableCampaigns(campaignsRes.data?.campaigns || []);
      setAvailableForms(formsRes.data?.forms || []);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to load mappings");
      toast({
        title: "Error",
        description: "Failed to load vertical mappings",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Create new mapping
  const handleCreate = async () => {
    if (!newMappingId || !newMappingVertical) {
      toast({
        title: "Missing fields",
        description: "Please select both a campaign/form and a vertical",
        variant: "destructive",
      });
      return;
    }
    
    setCreating(true);
    try {
      const payload = {
        platform: "meta",
        mapping_type: newMappingType,
        vertical: newMappingVertical,
      };
      
      if (newMappingType === "campaign") {
        payload.meta_campaign_id = newMappingId;
      } else {
        payload.meta_form_id = newMappingId;
      }
      
      await api.post('/vertical-mappings', payload);
      
      toast({
        title: "Mapping created",
        description: "New vertical mapping has been saved",
      });
      
      setCreateDialogOpen(false);
      setNewMappingId("");
      setNewMappingVertical("");
      fetchData();
    } catch (err) {
      toast({
        title: "Error",
        description: err.response?.data?.detail || "Failed to create mapping",
        variant: "destructive",
      });
    } finally {
      setCreating(false);
    }
  };

  // Delete mapping
  const handleDelete = async () => {
    if (!selectedMapping) return;
    
    try {
      await api.delete(`/vertical-mappings/${selectedMapping.id}`);
      
      toast({
        title: "Mapping deleted",
        description: "Vertical mapping has been removed",
      });
      
      setDeleteDialogOpen(false);
      setSelectedMapping(null);
      fetchData();
    } catch (err) {
      toast({
        title: "Error",
        description: err.response?.data?.detail || "Failed to delete mapping",
        variant: "destructive",
      });
    }
  };

  // Get available options for dropdown based on type
  // Filter out options with empty/null IDs to prevent Select.Item errors
  const getAvailableOptions = () => {
    if (newMappingType === "campaign") {
      return availableCampaigns.filter(c => !c.is_mapped && c.meta_campaign_id);
    } else {
      return availableForms.filter(f => !f.is_mapped && f.meta_form_id);
    }
  };

  const getVerticalLabel = (vertical) => {
    const found = VALID_VERTICALS.find(v => v.value === vertical);
    return found ? found.label : vertical;
  };

  return (
    <div className="space-y-6" data-testid="vertical-mappings-manager">
      <Card className="glass border-white/10">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg text-slate-100 flex items-center gap-2">
                <MapPin className="h-5 w-5 text-indigo-400" />
                Vertical Mappings
              </CardTitle>
              <CardDescription className="text-slate-400 mt-1">
                Map Meta campaigns and forms to verticals for automatic lead classification.
                New leads will be auto-assigned the vertical when they match a mapping.
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={fetchData}
                disabled={loading}
                data-testid="vertical-mappings-refresh-btn"
              >
                <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
              <Button
                size="sm"
                onClick={() => setCreateDialogOpen(true)}
                data-testid="add-mapping-btn"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Mapping
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {error && (
            <div className="flex items-center gap-2 text-red-400 mb-4 p-3 bg-red-500/10 rounded-lg">
              <AlertCircle className="h-4 w-4" />
              <span className="text-sm">{error}</span>
            </div>
          )}
          
          {mappings.length === 0 ? (
            <div className="text-center py-8 text-slate-400">
              <MapPin className="h-12 w-12 mx-auto mb-3 opacity-30" />
              <p>No vertical mappings configured yet.</p>
              <p className="text-sm mt-1">
                Add a mapping to automatically classify new leads by campaign or form.
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-white/10">
                  <TableHead className="text-slate-300">Type</TableHead>
                  <TableHead className="text-slate-300">Campaign / Form</TableHead>
                  <TableHead className="text-slate-300">Vertical</TableHead>
                  <TableHead className="text-slate-300 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {mappings.map((mapping) => (
                  <TableRow key={mapping.id} className="border-white/10">
                    <TableCell>
                      <Badge 
                        variant="outline" 
                        className={mapping.mapping_type === "campaign" 
                          ? "bg-sky-500/30 text-sky-200 border-sky-400/40"
                          : "bg-violet-500/30 text-violet-200 border-violet-400/40"
                        }
                      >
                        {mapping.mapping_type === "campaign" ? (
                          <><Layers className="h-3 w-3 mr-1" /> Campaign</>
                        ) : (
                          <><FileText className="h-3 w-3 mr-1" /> Form</>
                        )}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-slate-100 font-medium">
                      {mapping.mapping_type === "campaign" 
                        ? (mapping.meta_campaign_name || mapping.meta_campaign_id)
                        : (mapping.meta_form_name || mapping.meta_form_id)
                      }
                    </TableCell>
                    <TableCell>
                      <Badge className="bg-teal-500/30 text-teal-200 border-teal-400/40">
                        {getVerticalLabel(mapping.vertical)}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setSelectedMapping(mapping);
                          setDeleteDialogOpen(true);
                        }}
                        className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                        data-testid={`delete-mapping-${mapping.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Unmapped Campaigns/Forms Preview */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="glass border-white/10">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-slate-200 flex items-center gap-2">
              <Layers className="h-4 w-4 text-blue-400" />
              Unmapped Campaigns
              <Badge variant="outline" className="ml-2 text-xs bg-slate-700/50 text-slate-200 border-slate-500/50">
                {availableCampaigns.filter(c => !c.is_mapped).length} available
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="max-h-48 overflow-y-auto">
            {availableCampaigns.filter(c => !c.is_mapped).length === 0 ? (
              <p className="text-sm text-slate-400">All campaigns are mapped</p>
            ) : (
              <div className="space-y-1">
                {availableCampaigns.filter(c => !c.is_mapped).slice(0, 5).map(c => (
                  <div key={c.meta_campaign_id} className="flex items-center justify-between text-sm py-1">
                    <span className="text-slate-300 truncate flex-1">{c.meta_campaign_name}</span>
                    <Badge variant="outline" className="text-xs ml-2 bg-slate-700/50 text-slate-200 border-slate-500/50">{c.lead_count} leads</Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="glass border-white/10">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-slate-200 flex items-center gap-2">
              <FileText className="h-4 w-4 text-purple-400" />
              Unmapped Forms
              <Badge variant="outline" className="ml-2 text-xs bg-slate-700/50 text-slate-200 border-slate-500/50">
                {availableForms.filter(f => !f.is_mapped).length} available
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="max-h-48 overflow-y-auto">
            {availableForms.filter(f => !f.is_mapped).length === 0 ? (
              <p className="text-sm text-slate-400">All forms are mapped or no forms available</p>
            ) : (
              <div className="space-y-1">
                {availableForms.filter(f => !f.is_mapped).slice(0, 5).map(f => (
                  <div key={f.meta_form_id} className="flex items-center justify-between text-sm py-1">
                    <span className="text-slate-300 truncate flex-1">{f.meta_form_name}</span>
                    <Badge variant="outline" className="text-xs ml-2 bg-slate-700/50 text-slate-200 border-slate-500/50">{f.lead_count} leads</Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Create Mapping Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="bg-slate-950 text-slate-100 ring-1 ring-white/10 sm:max-w-md" data-testid="create-mapping-dialog">
          <DialogHeader>
            <DialogTitle className="text-slate-100">Create Vertical Mapping</DialogTitle>
            <DialogDescription className="text-slate-400">
              Map a Meta campaign or form to a vertical. New leads from this source will be automatically classified.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-200">Mapping Type</label>
              <Select value={newMappingType} onValueChange={(v) => {
                setNewMappingType(v);
                setNewMappingId("");
              }}>
                <SelectTrigger className="bg-white/5 border-white/10 text-slate-100" data-testid="mapping-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-700">
                  <SelectItem value="campaign" className="text-slate-100">
                    <span className="flex items-center gap-2">
                      <Layers className="h-4 w-4" /> Campaign
                    </span>
                  </SelectItem>
                  <SelectItem value="form" className="text-slate-100">
                    <span className="flex items-center gap-2">
                      <FileText className="h-4 w-4" /> Form
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-200">
                {newMappingType === "campaign" ? "Campaign" : "Form"}
              </label>
              <Select value={newMappingId} onValueChange={setNewMappingId}>
                <SelectTrigger className="bg-white/5 border-white/10 text-slate-100" data-testid="mapping-source-select">
                  <SelectValue placeholder={`Select ${newMappingType}...`} />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-700">
                  {getAvailableOptions().length === 0 ? (
                    <div className="px-2 py-4 text-sm text-slate-400 text-center">
                      No unmapped {newMappingType}s available
                    </div>
                  ) : (
                    getAvailableOptions().map(opt => {
                      const optValue = newMappingType === "campaign" 
                        ? opt.meta_campaign_id 
                        : opt.meta_form_id;
                      const optName = newMappingType === "campaign" 
                        ? opt.meta_campaign_name 
                        : opt.meta_form_name;
                      
                      // Skip if value is empty (defensive)
                      if (!optValue) return null;
                      
                      return (
                        <SelectItem 
                          key={optValue}
                          value={optValue}
                          className="text-slate-100"
                        >
                          <span className="flex items-center justify-between w-full">
                            <span className="truncate">
                              {optName || optValue}
                            </span>
                            <span className="text-xs text-slate-400 ml-2">
                              ({opt.lead_count} leads)
                            </span>
                          </span>
                        </SelectItem>
                      );
                    })
                  )}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-200">Vertical</label>
              <Select value={newMappingVertical} onValueChange={setNewMappingVertical}>
                <SelectTrigger className="bg-white/5 border-white/10 text-slate-100" data-testid="mapping-vertical-select">
                  <SelectValue placeholder="Select vertical..." />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-700">
                  {VALID_VERTICALS.map(v => (
                    <SelectItem key={v.value} value={v.value} className="text-slate-100">
                      {v.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="secondary" onClick={() => setCreateDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleCreate} 
              disabled={creating || !newMappingId || !newMappingVertical}
              data-testid="save-mapping-btn"
            >
              {creating ? "Creating..." : "Create Mapping"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Mapping?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove the vertical mapping. New leads from this {selectedMapping?.mapping_type} will no longer be auto-classified.
              Existing leads will not be affected.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-500 hover:bg-red-600"
              data-testid="confirm-delete-mapping"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default VerticalMappingsManager;
